import { useEffect, useState } from "react";
import { ArrowRight, Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { contactsService } from "@/services/contactsService";
import { getApiErrorMessage } from "@/utils/apiErrors";
import type { ContactRef, ContactTransfer } from "@/types/contactDeletion";

interface Props {
  open: boolean;
  contactId: string;
  /** Target suggested by the duplicate error, if it resolved to an Atlas contact. */
  suggested?: ContactRef | null;
  /** CRM id from the duplicate error, used to pre-fill the search. */
  suggestedExternalId?: string | null;
  onClose: () => void;
  onTransferred: (result: ContactTransfer) => void;
}

function refLabel(ref: ContactRef): string {
  return ref.name?.trim() || "(unnamed)";
}

/**
 * Moves a duplicate contact's notes and diary entries onto the record the CRM kept.
 *
 * The target is always shown as CRM id **and** name: two contacts in one agency
 * frequently share a name, and the id is the only thing that tells them apart — which
 * matters when the action is irreversible.
 */
export function ContactTransferDialog({
  open,
  contactId,
  suggested,
  suggestedExternalId,
  onClose,
  onTransferred,
}: Props) {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<ContactRef[]>([]);
  const [selected, setSelected] = useState<ContactRef | null>(null);
  const [preview, setPreview] = useState<ContactTransfer | null>(null);
  const [searching, setSearching] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start from whatever the duplicate error pointed at, so the common case needs no
  // searching at all.
  useEffect(() => {
    if (!open) return;
    setSelected(suggested ?? null);
    setQuery(suggested ? "" : (suggestedExternalId ?? ""));
    setPreview(null);
    setError(null);
    setCandidates([]);
  }, [open, suggested, suggestedExternalId]);

  // Debounced search — the operator types a CRM id or part of a name.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      setSearching(true);
      contactsService
        .findTransferCandidates(contactId, query || undefined)
        .then((rows) => {
          if (!cancelled) setCandidates(rows);
        })
        .catch((e: unknown) => {
          if (!cancelled) setError(getApiErrorMessage(e) ?? "Could not load contacts.");
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, contactId, query]);

  // Any change of target invalidates the counts, so re-run the dry run rather than
  // leaving numbers on screen that describe a different contact.
  useEffect(() => {
    if (!open || !selected?.externalId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    setError(null);
    contactsService
      .previewTransfer(contactId, selected.externalId)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(getApiErrorMessage(e) ?? "Could not preview the transfer.");
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, contactId, selected]);

  if (!open) return null;

  const runTransfer = async () => {
    if (!selected?.externalId) return;
    setTransferring(true);
    setError(null);
    try {
      const result = await contactsService.transferHistory(contactId, selected.externalId);
      onTransferred(result);
      onClose();
    } catch (e: unknown) {
      setError(getApiErrorMessage(e) ?? "The transfer failed. Nothing was moved.");
    } finally {
      setTransferring(false);
    }
  };

  const busy = transferring || previewing;
  const movesNothing = !!preview && preview.notesTransferred === 0 && preview.diaryEntriesTransferred === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => !busy && onClose()}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Transfer notes and diary entries"
        className="relative mx-4 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-lg"
      >
        <div className="flex items-start justify-between gap-4 border-b border-bridge-border/60 px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-bridge-fg">Transfer notes &amp; diary entries</h2>
            <p className="mt-0.5 text-xs text-bridge-muted">
              Move this contact's history onto the contact the CRM kept, so deleting it loses nothing.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="shrink-0 rounded-lg p-1.5 text-bridge-muted transition-colors hover:text-bridge-fg disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
          {error && (
            <div className="rounded-lg border border-bridge-danger/30 bg-red-50 px-3 py-2 text-sm text-bridge-danger">
              {error}
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-bridge-muted">
              Transfer to
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-bridge-subtle" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by CRM ID or name"
                className="pl-9"
              />
            </div>
          </div>

          <ul className="max-h-56 divide-y divide-bridge-border/60 overflow-y-auto rounded-lg border border-bridge-border/60">
            {searching && candidates.length === 0 && (
              <li className="flex items-center gap-2 px-3 py-3 text-sm text-bridge-muted">
                <Loader2 size={14} className="animate-spin" /> Searching…
              </li>
            )}
            {!searching && candidates.length === 0 && (
              <li className="px-3 py-3 text-sm text-bridge-muted">
                No other CRM-backed contacts found in this account.
              </li>
            )}
            {candidates.map((c) => {
              const isSelected = selected?.id === c.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(c)}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                      isSelected ? "bg-bridge-accent-tint" : "hover:bg-slate-50"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-800">
                        {refLabel(c)}
                      </span>
                      {/* The CRM id is the identifying detail, not decoration. */}
                      <span className="block truncate text-xs text-bridge-muted">
                        CRM ID {c.externalId ?? "—"}
                      </span>
                    </span>
                    {isSelected && (
                      <span className="shrink-0 rounded-full bg-[#009BF5]/10 px-2 py-0.5 text-[10px] font-medium text-[#009BF5]">
                        selected
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {selected && (
            <div className="rounded-lg border border-bridge-border/60 bg-slate-50 px-3 py-2.5">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <span className="min-w-0 truncate">{preview?.source ? refLabel(preview.source) : "This contact"}</span>
                <ArrowRight size={14} className="shrink-0 text-bridge-subtle" />
                <span className="min-w-0 truncate font-semibold">
                  {refLabel(selected)} <span className="font-normal text-bridge-muted">({selected.externalId})</span>
                </span>
              </div>

              {previewing ? (
                <div className="mt-2 flex items-center gap-2 text-sm text-bridge-muted">
                  <Loader2 size={14} className="animate-spin" /> Checking what would move…
                </div>
              ) : preview ? (
                <ul className="mt-2 space-y-1">
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">Related Notes</span>
                    <span className="tabular-nums font-semibold text-slate-800">
                      {preview.notesTransferred}
                    </span>
                  </li>
                  <li className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">Diary Entries</span>
                    <span className="tabular-nums font-semibold text-slate-800">
                      {preview.diaryEntriesTransferred}
                    </span>
                  </li>
                  {preview.notesAlreadyOnTarget + preview.diaryEntriesAlreadyOnTarget > 0 && (
                    <li className="pt-1 text-xs text-bridge-muted">
                      {preview.notesAlreadyOnTarget + preview.diaryEntriesAlreadyOnTarget} link(s) dropped —
                      that contact is already on those records.
                    </li>
                  )}
                </ul>
              ) : null}
            </div>
          )}

          {movesNothing && (
            <p className="text-sm text-bridge-muted">
              Nothing to move — this contact has no notes or diary entries.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-bridge-border/60 bg-gray-50 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={runTransfer}
            loading={transferring}
            // Requires a target and a settled dry run: transferring on stale or absent
            // counts would move records the operator never agreed to.
            disabled={!selected || previewing || !preview || movesNothing}
          >
            Transfer
          </Button>
        </div>
      </div>
    </div>
  );
}
